import React, { useState } from 'react';
import styled from 'styled-components';
import { formatPhoneNumber, isValidPhone } from '../../utils/validation';

interface InviteRefereeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInvite: (data: InviteRefereeData) => Promise<void>;
}

export interface InviteRefereeData {
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  gameTypes: string[];
  isMainReferee: boolean;
}

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #1a2a1a;
  border: 1px solid #00d4ff;
  border-radius: 8px;
  padding: 30px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
`;

const ModalTitle = styled.h2`
  color: #00d4ff;
  margin: 0;
  font-size: 1.5rem;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: #00d4ff;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.1);
  }
`;


const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  color: #b0b0b0;
  margin-bottom: 8px;
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  background: #0a0f0a;
  border: 1px solid #333;
  border-radius: 4px;
  color: white;
  font-size: 1rem;
  transition: border-color 0.3s;

  &:focus {
    outline: none;
    border-color: #00d4ff;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
`;

const CheckboxItem = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #ffffff;
  font-size: 0.9rem;
  cursor: pointer;
  padding: 8px;
  border: 1px solid #333;
  border-radius: 4px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(0, 212, 255, 0.1);
    border-color: #00d4ff;
  }
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
`;

const RoleToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
  padding: 10px;
  border: 1px solid #333;
  border-radius: 4px;
  background: #0a0f0a;
`;

const ToggleLabel = styled.span`
  color: #b0b0b0;
  font-size: 0.9rem;
`;

const Toggle = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  margin-top: 30px;
`;

const CancelButton = styled.button`
  background: transparent;
  border: 1px solid #666;
  color: #b0b0b0;
  padding: 10px 25px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.3s;

  &:hover {
    border-color: #00d4ff;
    color: #00d4ff;
  }
`;

const SubmitButton = styled.button`
  background: linear-gradient(135deg, #00d4ff, #0099cc);
  border: none;
  color: white;
  padding: 10px 25px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: bold;
  transition: all 0.3s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 212, 255, 0.3);
  }

  &:disabled {
    background: #666;
    cursor: not-allowed;
    transform: none;
  }
`;

const ErrorMessage = styled.div`
  color: #ff3333;
  font-size: 0.9rem;
  margin-top: 10px;
  padding: 10px;
  background: rgba(255, 51, 51, 0.1);
  border: 1px solid #ff3333;
  border-radius: 4px;
`;

const GAME_TYPES = [
  { value: 'airsoft', label: 'Airsoft' },
  { value: 'paintball', label: 'Paintball' },
  { value: 'speedball', label: 'Speedball' }
];

const InviteRefereeModal: React.FC<InviteRefereeModalProps> = ({ isOpen, onClose, onInvite }) => {
  const [formData, setFormData] = useState<InviteRefereeData>({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    gameTypes: [],
    isMainReferee: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const isValidChar = /^[0-9\s()\-+]*$/.test(inputValue);

    if (isValidChar) {
      const formatted = formatPhoneNumber(inputValue);
      setFormData(prev => ({ ...prev, phone: formatted }));
    }
    setError('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleGameTypeChange = (gameType: string) => {
    setFormData(prev => ({
      ...prev,
      gameTypes: prev.gameTypes.includes(gameType)
        ? prev.gameTypes.filter(gt => gt !== gameType)
        : [...prev.gameTypes, gameType]
    }));
    setError('');
  };

  const handleMainRefereeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, isMainReferee: e.target.checked }));
  };

  const validateForm = () => {
    if (!formData.email || !formData.firstName) {
      setError('Email and first name are required');
      return false;
    }

    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }

    if (formData.phone && !isValidPhone(formData.phone)) {
      setError('Please enter a valid phone number (min 10 digits)');
      return false;
    }

    if (formData.gameTypes.length === 0) {
      setError('Please select at least one game type');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      await onInvite(formData);
      // Reset form on success
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        phone: '',
        gameTypes: [],
        isMainReferee: false
      });
      onClose();
    } catch (error) {
      setError('Failed to invite referee');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        phone: '',
        gameTypes: [],
        isMainReferee: false
      });
      setError('');
      onClose();
    }
  };

  return (
    <ModalOverlay $isOpen={isOpen} onClick={handleClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Invite Referee</ModalTitle>
          <CloseButton onClick={handleClose} disabled={loading}>×</CloseButton>
        </ModalHeader>
        
        <form onSubmit={handleSubmit}>
            <FormGroup>
              <Label>Email Address *</Label>
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="referee@example.com"
                disabled={loading}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>First Name *</Label>
              <Input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                placeholder="John"
                disabled={loading}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>Last Name</Label>
              <Input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                placeholder="Doe"
                disabled={loading}
              />
            </FormGroup>

            <FormGroup>
              <Label>Phone Number</Label>
              <Input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handlePhoneChange}
                placeholder="+1 (555) 123-4567"
                disabled={loading}
                maxLength={17}
              />
            </FormGroup>

            <FormGroup>
              <Label>Game Types *</Label>
              <CheckboxGroup>
                {GAME_TYPES.map(gameType => (
                  <CheckboxItem key={gameType.value}>
                    <Checkbox
                      type="checkbox"
                      checked={formData.gameTypes.includes(gameType.value)}
                      onChange={() => handleGameTypeChange(gameType.value)}
                      disabled={loading}
                    />
                    {gameType.label}
                  </CheckboxItem>
                ))}
              </CheckboxGroup>
            </FormGroup>

            <FormGroup>
              <Label>Role</Label>
              <RoleToggle>
                <Toggle
                  type="checkbox"
                  checked={formData.isMainReferee}
                  onChange={handleMainRefereeChange}
                  disabled={loading}
                />
                <ToggleLabel>Main Referee</ToggleLabel>
              </RoleToggle>
            </FormGroup>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <ButtonGroup>
              <CancelButton type="button" onClick={handleClose} disabled={loading}>
                Cancel
              </CancelButton>
              <SubmitButton type="submit" disabled={loading}>
                {loading ? 'Sending Invitation...' : 'Send Invitation'}
              </SubmitButton>
            </ButtonGroup>
          </form>
      </ModalContent>
    </ModalOverlay>
  );
};

export default InviteRefereeModal;